import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import { PAYMENT_MODEL_ENUM } from '@nxsys/constants/AppEnums';
import { useGetAgencyStatistics } from '@nxsys/hooks/api/dashboard';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { Col, Row } from 'antd';
import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import AgencyPayment from './AgencyPayment';
import AgencyRegistration from './AgencyRegistration';
import AgencyTimesheets from './AgencyTimesheets';
import AgencyUploadFile from './AgencyUploadFile';
import CurrentEmploymentYTD from './components/CurrentEmploymentYTD';
import DocumentNearingExpiry from './components/DocumentNearingExpiry';
import EmployeeDashboardTimesheet from './components/EmployeeDashboardTimesheet';
import EmployeeOtherItem from './components/EmployeeOtherItem';
import EmployeePensionContribution from './components/EmployeePensionContribution';
import HolidayPayBalance from './components/HolidayPayBalance';
import PayBreakdown from './components/PayBreakdown';
import PayslipHistory from './components/PayslipHistory';
import UpcomingPayment from './components/UpcomingPayment';
import EmployeeSubcontractor from './EmployeeSubcontractor';
import OnHold from './OnHold';
import Payments from './Payments';
import PensionContribution from './PensionContribution';
import RevenueSubmission from './RevenueSubmission';
import RTIFilling from './RTIFilling';
import BannerCarouselSection from './components/BannerCarouselSection';
import Timesheets from './Timesheets';
import { getGreetingDashboard } from '@nxsys/utils';
import YearEndAlert from '@nxsys/components/YearEndProcess/YearEndAlert';

const DashboardUmbrella = () => {
  const { user, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;

  const renderTitle = useCallback(() => {
    return (
      <>
        <AppPageTitle
          separator=""
          prefix={getGreetingDashboard()}
          title={`${user?.forename || ''} ${user?.surname || ''}`}
        />
        <YearEndAlert />
      </>
    );
  }, [user]);

  // Agency
  const isAgency = user?.roles === 'agency';
  const agencyId = (user?.agencyId as any)?.id;
  const {
    value: agencyValue,
    loading: agencyLoading,
    run: agencyRun,
  } = useGetAgencyStatistics();
  const agencyData = useMemo(() => agencyValue?.data, [agencyValue]);

  // Subcontractor
  const isSubcontractor = user?.roles === 'subcontractor';

  useEffect(() => {
    if (isAgency) {
      agencyRun({
        agencyIds: [agencyId],
        agencyRefs: [agencyId],
      });
    }
  }, [isAgency]);

  const renderDashboardByRole = () => {
    // Agency
    if (isAgency) {
      return (
        <>
          <BannerCarouselSection />
          <AgencyRegistration
            loading={agencyLoading}
            data={agencyData?.registrations}
            agencyId={agencyId}
          />
          <Row gutter={[16, 16]}>
            <Col sm={24} md={16}>
              <AgencyPayment
                loading={agencyLoading}
                data={agencyData?.payment}
                agencyId={agencyId}
              />
            </Col>
            <Col sm={24} md={8}>
              <AgencyTimesheets
                loading={agencyLoading}
                data={agencyData?.timesheet}
                agencyId={agencyId}
              />
            </Col>
          </Row>
        </>
      );
    }

    // Subcontractor
    if (isSubcontractor) {
      const subcontractorId = (user?.subcontractorId as any)?.id;
      const isCisOrNonCis =
        (user?.subcontractorId as any)?.paymentModel ===
          PAYMENT_MODEL_ENUM.CIS ||
        (user?.subcontractorId as any)?.paymentModel ===
          PAYMENT_MODEL_ENUM.NON_CIS;

      return (
        <>
          <BannerCarouselSection />
          <Row gutter={[16, 16]}>
            <Col sm={24} md={6} className="w-full">
              <UpcomingPayment
                subcontractorId={subcontractorId}
                isCisOrNonCis={isCisOrNonCis}
              />
            </Col>
            <Col sm={24} md={12} className="w-full">
              <PayBreakdown />
            </Col>
            <Col sm={24} md={6} className="w-full">
              <PayslipHistory />
            </Col>
          </Row>
          {!isCisOrNonCis && (
            <Row gutter={[16, 16]}>
              <Col sm={24} md={12} className="w-full">
                <CurrentEmploymentYTD />
              </Col>
              <Col sm={24} md={12} className="w-full">
                <EmployeeOtherItem />
              </Col>
            </Row>
          )}
          <EmployeeDashboardTimesheet />
          {!isCisOrNonCis && (
            <Row gutter={[16, 16]}>
              <Col sm={24} md={12} className="w-full">
                <HolidayPayBalance />
              </Col>
              <Col sm={24} md={12} className="w-full">
                <EmployeePensionContribution />
              </Col>
            </Row>
          )}
          <DocumentNearingExpiry subcontractorId={subcontractorId} />
        </>
      );
    }

    // Admin
    return (
      <>
        <BannerCarouselSection />
        {/* NX-5228: RTI (CIS 300 / FPS / EPS) is HMRC, so it is UK-only. An
            Irish tenant files PSR with Revenue instead, and the rti-filings
            routes it linked to are not even registered for umbrella-Ireland,
            so those tiles dead-ended. RevenueSubmission is the card the bureau
            Ireland dashboard already uses and takes no props. */}
        {isIreland ? <RevenueSubmission dark /> : <RTIFilling />}
        <Timesheets />
        {/* NX-6253: the Irish employee card, Payroll Imports and Client
            Approval & Reject that NX-5228 added here are hidden again at the
            client's request; RPN and AEPN updates stay reachable from the
            sidebar. */}
        <Payments />
        <Row gutter={[16, 16]}>
          <Col sm={24} md={12}>
            <PensionContribution />
          </Col>
          <Col sm={24} md={12}>
            <OnHold />
          </Col>
        </Row>
        <EmployeeSubcontractor />
      </>
    );
  };

  const renderRightContent = useCallback(() => {
    if (isAgency) {
      return <AgencyUploadFile agencyId={agencyId} />;
    }
    return null;
  }, [agencyId]);

  return (
    <AppsContainer
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={false}
      fullView
    >
      <div className="space-y-[24px] pb-[24px]">{renderDashboardByRole()}</div>
    </AppsContainer>
  );
};

export default DashboardUmbrella;
